import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { fleetService } from '../../services/fleetService';
import UserStatusBadge from '../../components/users/UserStatusBadge';
import {
    ChevronLeft,
    User,
    Mail,
    Phone,
    Calendar,
    Shield,
    Key,
    Users,
    Edit,
    AlertTriangle
} from 'lucide-react';

const UserDetailPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [fleets, setFleets] = useState([]);
    const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);

    useEffect(() => {
        fetchUserDetails();
        if (currentUser.user_type === 'admin') {
            fetchFleets();
        }
    }, [userId]);

    const fetchUserDetails = async () => {
        setIsLoading(true);
        try {
            const userData = await userService.getUserById(userId);
            setUser(userData);
        } catch (error) {
            console.error('Error fetching user details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await userService.updateUserStatus(userId, newStatus);
            fetchUserDetails(); // 重新加載用戶詳情
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleResetPassword = async () => {
        try {
            await userService.resetPassword(userId);
            setShowResetPasswordConfirm(false);
            // 可以顯示成功提示
        } catch (error) {
            console.error('Error resetting password:', error);
        }
    };

    const getUserTypeLabel = (userType) => {
        const userTypeMap = {
            'admin': '系統管理員',
            'dispatcher': '調度員',
            'fleet_manager': '車隊經理',
            'support': '技術支援'
        };
        return userTypeMap[userType] || userType;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">找不到用戶</h2>
                <p className="text-gray-600 mb-4">無法找到ID為 {userId} 的用戶資訊。</p>
                <button
                    onClick={() => navigate('/users')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    返回用戶列表
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => navigate('/users')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">用戶詳情</h1>
                <UserStatusBadge status={user.status} className="ml-4" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 用戶基本資訊卡片 */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold">基本資訊</h3>
                            <button
                                onClick={() => navigate(`/users/${userId}/edit`)}
                                className="text-blue-500 hover:text-blue-700"
                            >
                                <Edit className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="flex flex-col items-center mb-6">
                            <div className="w-24 h-24 bg-gray-200 rounded-full mb-4 overflow-hidden">
                                {user.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt={user.username}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-500">
                                        <User className="w-12 h-12" />
                                    </div>
                                )}
                            </div>
                            <h4 className="text-xl font-bold">{user.username}</h4>
                            <p className="text-gray-600">{getUserTypeLabel(user.user_type)}</p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center">
                                <Mail className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">電子郵件</p>
                                    <p>{user.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">聯絡電話</p>
                                    <p>{user.phone || '未提供'}</p>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                    <p className="text-sm text-gray-500">加入時間</p>
                                    <p>{new Date(user.created_at).toLocaleDateString('zh-TW')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 右側卡片 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 帳戶狀態管理 */}
                    {currentUser.user_type === 'admin' && (
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h3 className="font-bold">帳戶狀態管理</h3>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <button
                                        onClick={() => handleStatusChange('active')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${user.status === 'active'
                                            ? 'bg-green-500 text-white'
                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        啟用帳戶
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('inactive')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${user.status === 'inactive'
                                            ? 'bg-red-500 text-white'
                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        停用帳戶
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('pending')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${user.status === 'pending'
                                            ? 'bg-yellow-500 text-white'
                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        待審核
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('suspended')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${user.status === 'suspended'
                                            ? 'bg-gray-500 text-white'
                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        暫停
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 權限資訊 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h3 className="font-bold">權限資訊</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <h4 className="font-medium mb-2">角色權限</h4>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                                        {getUserTypeLabel(user.user_type)}
                                    </span>
                                    {user.fleet_id && (
                                        <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm">
                                            {fleets.find(f => f.fleet_id === user.fleet_id)?.fleet_name || '車隊'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium mb-2">登入資訊</h4>
                                <div className="bg-gray-50 p-3 rounded-md">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-gray-600">最後登入時間</span>
                                        <span className="text-sm">
                                            {user.last_login
                                                ? new Date(user.last_login).toLocaleString('zh-TW')
                                                : '從未登入'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">最後登入IP</span>
                                        <span className="text-sm">
                                            {user.last_login_ip || '未知'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* 密碼重置 */}
                            {currentUser.user_type === 'admin' && (
                                <div>
                                    <h4 className="font-medium mb-2">帳戶安全</h4>
                                    <button
                                        onClick={() => setShowResetPasswordConfirm(true)}
                                        className="w-full bg-red-50 text-red-600 py-2 rounded-md hover:bg-red-100 flex items-center justify-center"
                                    >
                                        <Key className="w-5 h-5 mr-2" />
                                        重置密碼
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 密碼重置確認彈窗 */}
            {showResetPasswordConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0 mt-0.5">
                                <AlertTriangle className="h-6 w-6 text-yellow-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">確認重置密碼</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    您確定要為此用戶重置密碼嗎？這將生成一個臨時密碼，並要求用戶在下次登入時更改密碼。
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowResetPasswordConfirm(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleResetPassword}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                            >
                                確認重置
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDetailPage;